import {
  CatalogResourceDc,
  CatalogResourceType,
  EqlAttributeConfigurationDc,
  QueryLayerServiceConfigurationDc,
  QueryLayerServiceInfoDc,
} from "@evergis/api";
import { IconButton, RaisedButton } from "@evergis/ui";
import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { layerDependencies } from "../../../../../resources/layers";
import { SYS_ATTRIBUTES } from "../../../DialogComponents/Attributes/const";
import isValidLayerReference from "../../../DialogComponents/Attributes/LayerReferences/isValidLayerReference";
// eslint-disable-next-line max-len
import { useLayerReferencesContext } from "../../../DialogComponents/Attributes/LayerReferences/LayerReferencesProvider";
import { ActionsDivider } from "../../../DialogComponents/Attributes/styled";
import { SystemAttributeOption } from "../../../DialogComponents/Attributes/types";
import { EditResource } from "../../../SelectFiles/Edit/EditResource";
import { useEditResource } from "../../../SelectFiles/Edit/useEditResource";
import { StyledActionsButtonsGroup } from "../../../SelectFiles/styled";
import { useNewAttribute } from "./hooks/useNewAttribute";
import { useQueryAttributes } from "./hooks/useQueryAttributes";
import { useQuerySysAttributes } from "./hooks/useQuerySysAttributes";
import { QueryLayerAttributes } from "./QueryLayerAttributes";
import { StyledActionsBar, StyledAddSystemAttributes, StyledContent, StyledLayerReferences } from "./styled";
import { LayerResourceMode } from "./types";
import { useUpdateQueryLayer } from "./hooks/useUpdateQueryLayer";

export interface LayerEditProps {
  layer: QueryLayerServiceInfoDc;
  mode: LayerResourceMode;
}

// eslint-disable-next-line max-lines-per-function
export const QueryLayerEdit: FC<LayerEditProps> = ({ layer, mode }) => {
  const { t } = useTranslation("common");

  const {
    attributesConfiguration,
    attributes,
    setAttributes,
    updateAttribute,
    deleteAttribute,
    addAttribute,
    setDisplayField,
  } = useQueryAttributes((layer?.configuration as QueryLayerServiceConfigurationDc)?.attributesConfiguration);
  const [path, setPath] = useState<CatalogResourceDc[]>([]);

  const item = useMemo(
    (): CatalogResourceDc => ({
      name: layer?.alias || layer?.name,
      systemName: layer?.name,
      type: CatalogResourceType.Layer,
      geometryType: layer?.geometryType,
      tags: layer?.tags,
      description: layer?.description,
      resourceId: layer?.resourceId,
      parentId: layer?.parentId,
    }),
    [layer],
  );
  const { name, setName, description, setDescription, tags, setTags, systemName } = useEditResource(item);

  const parentId = useMemo(
    () => (path[path.length - 1] ? path[path.length - 1].resourceId : layer?.parentId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path[path.length - 1], layer?.parentId],
  );
  const updateMeta = useMemo(
    (): CatalogResourceDc => ({
      name,
      systemName,
      tags,
      description,
      parentId,
    }),
    [name, systemName, tags, description, parentId],
  );

  const { layerReferences, createLayerReference } = useLayerReferencesContext();

  const { data } = layerDependencies.use({ input: layer?.name, waitInput: true });

  const eqlTables = useMemo(
    () =>
      data ? data?.dependencies.filter(({ type }) => type === "Table").map(({ name: tableName }) => tableName) : [],
    [data],
  );
  const canAddDeleteAttributes = false;

  const { update, updating } = useUpdateQueryLayer({
    layer,
    updateMeta,
    attributesConfiguration,
    layerReferences,
    eqlTables,
  });

  const initialSysAttributes = useMemo(
    () => attributes?.filter(({ attributeName }) => SYS_ATTRIBUTES.includes(attributeName as SystemAttributeOption)),
    [attributes],
  );

  const [sysAttributes, { addSysAttributes, deleteSysAttribute }] = useQuerySysAttributes(
    initialSysAttributes,
    addAttribute,
    deleteAttribute,
  );

  const { newOpen, setNewOpen, onNewClose, onNewApply } = useNewAttribute<EqlAttributeConfigurationDc>(addAttribute);

  const allValid = layerReferences.every(isValidLayerReference);

  return (
    <StyledContent>
      {mode === LayerResourceMode.Info && (
        <EditResource
          item={item}
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          tags={tags}
          setTags={setTags}
          systemName={systemName}
          withPath
          path={path}
          setPath={setPath}
        />
      )}
      {mode === LayerResourceMode.Attributes && (
        <QueryLayerAttributes
          attributes={attributes}
          canDelete={canAddDeleteAttributes}
          updateAttribute={updateAttribute}
          setAttributes={setAttributes}
          deleteAttribute={deleteAttribute}
          deleteSysAttribute={deleteSysAttribute}
          idProperty={attributesConfiguration.idAttribute}
          geometryProperty={attributesConfiguration.geometryAttribute}
          displayField={attributesConfiguration.titleAttribute}
          setDisplayField={setDisplayField}
          layerReferences={layerReferences}
          geometryType={layer?.geometryType}
          newOpen={newOpen}
          onNewApply={onNewApply}
          onNewClose={onNewClose}
        />
      )}
      {mode === LayerResourceMode.RelatedData && <StyledLayerReferences withAddButton={false} />}
      <StyledActionsBar>
        {mode === LayerResourceMode.Attributes && canAddDeleteAttributes && (
          <StyledActionsButtonsGroup>
            <IconButton kind={"feature_add"} disabled={updating || newOpen} onClick={() => setNewOpen(true)}>
              {t("actions.add")}
            </IconButton>
            <ActionsDivider vertical />
            <StyledAddSystemAttributes sysAttributes={sysAttributes} addSysAttributes={addSysAttributes} />
          </StyledActionsButtonsGroup>
        )}
        {mode === LayerResourceMode.RelatedData && (
          <StyledActionsButtonsGroup>
            <IconButton kind={"feature_add"} disabled={!allValid} onClick={createLayerReference}>
              {t("actions.add")}
            </IconButton>
          </StyledActionsButtonsGroup>
        )}
        <StyledActionsButtonsGroup style={{ marginLeft: "auto" }}>
          <RaisedButton disabled={updating} onClick={update} primary>
            {t("actions.save")}
          </RaisedButton>
        </StyledActionsButtonsGroup>
      </StyledActionsBar>
    </StyledContent>
  );
};
